/**
 * FILE: SearchModal.js
 * PURPOSE: The "Find It" window.
 * WHAT IT DOES: It lets you search for polls or topics and shows you what is currently trending in the community.
 * LAYMAN SUMMARY: Use the top search bar to find exactly what you're looking for or browse popular topics.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useModalA11y } from '../hooks/useModalA11y';

export default function SearchModal({ isOpen, onClose, polls, onSelectPoll, onCreate, votedPolls = {} }) {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);

    // 300ms debounce — filter only runs after user stops typing
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const hasActiveSearch = !!debouncedQuery || !!activeCategory;

    // Memoised filter — only recomputes when the query, category, or polls change
    const filtered = useMemo(
        () => polls.filter(p =>
            (!debouncedQuery || p.question.toLowerCase().includes(debouncedQuery.toLowerCase())) &&
            (!activeCategory || p.category === activeCategory)
        ),
        [polls, debouncedQuery, activeCategory]
    );

    // Kept in sync with CreatePollModal's CATEGORIES list (see GlobalFeed.js for the same note).
    const categories = ['Tech & Future', 'Finance', 'Society', 'Lifestyle', 'Politics', 'Sports', 'Science', 'Health'];

    const handleTagClick = (tag) => {
        setActiveCategory(null);
        setQuery(tag.replace('#', ''));
    };

    const handleCategoryClick = (cat) => {
        setQuery('');
        setDebouncedQuery('');
        setActiveCategory(prev => prev === cat ? null : cat);
    };

    const containerRef = useModalA11y(isOpen, onClose);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[160] flex items-start justify-center pt-16 md:pt-24 px-4 overflow-hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}></div>
            <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Search polls" className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-modal overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-gray-100 dark:border-gray-800">

                {/* Header / Input */}
                <div className="p-5 md:p-8 md:pb-6 border-b border-gray-100 dark:border-gray-800 flex items-center space-x-3 md:space-x-4">
                    <Search className="text-primary-600 w-5 h-5 md:w-7 md:h-7 shrink-0" aria-hidden="true" />
                    <input
                        autoFocus
                        type="text"
                        aria-label="Search polls and topics"
                        placeholder="Search polls, topics, or insights..."
                        className="flex-1 bg-transparent text-lg md:text-2xl font-black italic uppercase tracking-tighter outline-none text-gray-900 dark:text-white placeholder:text-gray-200 dark:placeholder:text-gray-700 lowercase min-w-0"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <button onClick={onClose} aria-label="Close search" className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
                        <X size={24} className="text-gray-500" aria-hidden="true" />
                    </button>
                </div>

                <div className="p-0 max-h-[60vh] overflow-y-auto no-scrollbar" aria-live="polite" aria-atomic="true">

                    {/* Search Results (shown once debounce settles, or a category chip is active) */}
                    {hasActiveSearch && (
                        <div className="p-2">
                            {activeCategory && (
                                <div className="px-6 pt-3 pb-1 flex items-center gap-2">
                                    <span className="px-3 py-1 bg-primary-600/10 text-primary-600 rounded-full text-2xs font-black uppercase tracking-widest">{activeCategory}</span>
                                    <button onClick={() => setActiveCategory(null)} className="text-2xs font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white uppercase tracking-widest underline">Clear</button>
                                </div>
                            )}
                            {filtered.length > 0 ? (
                                <div className="space-y-1">
                                    <p className="px-6 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">
                                        Found {filtered.length} Existing Polls — Join the Conversation
                                    </p>
                                    {filtered.map(poll => (
                                        <div
                                            key={poll.id}
                                            onClick={() => { onSelectPoll(poll); onClose(); }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { onSelectPoll(poll); onClose(); } }}
                                            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-2xl cursor-pointer transition-colors flex items-center justify-between group"
                                        >
                                            <div>
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">{poll.question}</h4>
                                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{poll.category} &bull; {poll.type}</span>
                                            </div>
                                            <div className="px-4 py-2 bg-primary-600/10 text-primary-600 text-xs font-bold uppercase tracking-widest rounded-lg group-hover:bg-primary-600 group-hover:text-white transition-all">
                                                {votedPolls[poll.id] === 'revealed' ? 'View Results' : 'View Poll'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center space-y-4">
                                    <p className="text-sm font-semibold text-gray-500">
                                        {activeCategory ? `No polls found in "${activeCategory}"` : `No polls found matching “${debouncedQuery}”`}
                                    </p>
                                    {!activeCategory && (
                                        <button
                                            onClick={() => { onCreate(); onClose(); }}
                                            className="px-8 py-3 bg-primary-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:scale-105 transition-transform shadow-lg"
                                        >
                                            Create &ldquo;{debouncedQuery}&rdquo; Poll
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Default state: trending + categories */}
                    {!hasActiveSearch && (
                        <div className="p-6 space-y-8">
                            {/* Trending Topics */}
                            <div>
                                <h4 className="text-2xs md:text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Trending Now</h4>
                                <div className="flex flex-wrap gap-3">
                                    {['#AGI', '#Crypto', '#RemoteWork', '#ClimateAction', '#VR', '#MentalHealth'].map(tag => (
                                        <button key={tag} onClick={() => handleTagClick(tag)} className="px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-full text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest hover:bg-primary-600 hover:text-white transition-colors cursor-pointer dark:hover:bg-primary-600 dark:hover:text-white border border-transparent hover:border-primary-600">
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Browse by Topic */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Browse by Topic</h4>
                                <div className="flex flex-wrap gap-2">
                                    {categories.map(cat => (
                                        <button key={cat} onClick={() => handleCategoryClick(cat)} className="px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-primary-600 hover:text-white transition-colors dark:hover:bg-primary-600 dark:hover:text-white">
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
